import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

gsap?.registerPlugin(ScrollTrigger);

const ProblemSection = () => {
  const [activeView, setActiveView] = useState('before');
  const [language, setLanguage] = useState('en');
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setLanguage(savedLanguage);
  }, []);

  useEffect(() => {
    const ctx = gsap?.context(() => {
      // Animate cards on scroll
      cardsRef?.current?.forEach((card, index) => {
        if (card) {
          gsap?.fromTo(
            card,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              delay: index * 0.2,
              scrollTrigger: {
                trigger: card,
                start: 'top 80%',
                end: 'bottom 20%',
                toggleActions: 'play none none reverse',
              },
            }
          );
        }
      });
    }, sectionRef);

    return () => ctx?.revert();
  }, []);

  const content = {
    en: {
      title: 'The Hidden Cost of Booking Chaos',
      subtitle:
        'See how manual appointment management is costing your business',
      toggleBefore: 'Current Reality',
      toggleAfter: 'With Slotyfi',
      painPoints: [
        {
          icon: 'Clock',
          title: 'Time Wasted',
          stat: '4.2 hours daily',
          description:
            'Staff spend hours on phone calls, back-and-forth messages, and manual scheduling instead of serving customers.',
          color: 'red',
        },
        {
          icon: 'DollarSign',
          title: 'Revenue Lost',
          stat: '$2,847/month',
          description:
            'No-shows and last-minute cancellations drain revenue while empty slots remain unfilled.',
          color: 'orange',
        },
        {
          icon: 'Users',
          title: 'Customer Frustration',
          stat: '67% dissatisfied',
          description:
            'Customers abandon bookings due to complicated processes, long wait times, and poor communication.',
          color: 'yellow',
        },
        {
          icon: 'AlertTriangle',
          title: 'Staff Burnout',
          stat: '43% turnover',
          description:
            'Constant interruptions and scheduling conflicts lead to stressed staff and high turnover rates.',
          color: 'purple',
        },
      ],
      beforeScenario: {
        title: 'Manual Booking Nightmare',
        points: [
          'Phone rings constantly during service',
          'Double bookings and scheduling conflicts',
          'No-shows without warning',
          'Staff juggling multiple communication channels',
          'Lost appointments in messy calendars',
        ],
      },
      afterScenario: {
        title: 'Automated Booking Paradise',
        points: [
          'AI handles all booking requests 24/7',
          'Smart scheduling prevents conflicts',
          'Automated reminders reduce no-shows by 85%',
          'Unified dashboard for all channels',
          'Real-time calendar sync across devices',
        ],
      },
    },
    fa: {
      title: 'هزینه پنهان آشفتگی رزرو',
      subtitle: 'ببینید مدیریت دستی قرارملاقات چقدر به کسب‌وکارتان ضرر می‌زند',
      toggleBefore: 'وضعیت فعلی',
      toggleAfter: 'با اسلاتیفای',
      painPoints: [
        {
          icon: 'Clock',
          title: 'اتلاف وقت',
          stat: '۴.۵ ساعت روزانه',
          description:
            'کارکنان ساعت‌ها را صرف تماس‌های تلفنی، پیام‌های رفت و برگشت و برنامه‌ریزی دستی می‌کنند.',
          color: 'red',
        },
        {
          icon: 'DollarSign',
          title: 'از دست رفتن درآمد',
          stat: 'حداقل ۲۰ میلیون تومان ماهانه',
          description:
            'غیبت‌ها و لغو در آخرین لحظه درآمد را کاهش می‌دهد در حالی که زمان‌های خالی پر نمی‌شود.',
          color: 'orange',
        },
        {
          icon: 'Users',
          title: 'نارضایتی مشتری',
          stat: '۶۷% ناراضی',
          description:
            'مشتریان به دلیل فرآیندهای پیچیده، زمان انتظار طولانی و ارتباط ضعیف رزرو را رها می‌کنند.',
          color: 'yellow',
        },
        {
          icon: 'AlertTriangle',
          title: 'فرسودگی کارکنان',
          stat: '۴۳% جابجایی',
          description:
            'وقفه‌های مداوم و تضاد برنامه‌ریزی منجر به استرس کارکنان و نرخ بالای جابجایی می‌شود.',
          color: 'purple',
        },
      ],
      beforeScenario: {
        title: 'کابوس رزرو دستی',
        points: [
          'تلفن مدام در حین خدمات زنگ می‌زند',
          'رزرو مضاعف و تضاد برنامه‌ریزی',
          'غیبت بدون هشدار',
          'کارکنان با کانال‌های ارتباطی متعدد دست و پنجه نرم می‌کنند',
          'قرارملاقات‌های گم شده در تقویم‌های نامرتب',
        ],
      },
      afterScenario: {
        title: 'بهشت رزرو خودکار',
        points: [
          'هوش مصنوعی 24/7 درخواست‌های رزرو را مدیریت می‌کند',
          'برنامه‌ریزی هوشمند از تضاد جلوگیری می‌کند',
          'یادآوری‌های خودکار غیبت را 85% کاهش می‌دهد',
          'داشبورد یکپارچه برای همه کانال‌ها',
          'همگام‌سازی تقویم در زمان واقعی در همه دستگاه‌ها',
        ],
      },
    },
  };

  const currentContent = content?.[language];

  const getColorClasses = (color) => {
    const colors = {
      red: 'bg-red-50 border-red-200 text-red-700',
      orange: 'bg-orange-50 border-orange-200 text-orange-700',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
    };
    return colors?.[color] || colors?.red;
  };

  return (
    <section ref={sectionRef} className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div
          className={`text-center mb-16 ${language === 'fa' ? 'rtl' : 'ltr'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {currentContent?.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentContent?.subtitle}
          </p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {currentContent?.painPoints?.map((point, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className={`p-6 rounded-xl border-2 ${getColorClasses(
                point?.color
              )} hover:shadow-lg transition-all duration-300`}>
              <div className="flex items-center mb-4">
                <div
                  className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    point?.color === 'red'
                      ? 'bg-red-100'
                      : point?.color === 'orange'
                      ? 'bg-orange-100'
                      : point?.color === 'yellow'
                      ? 'bg-yellow-100'
                      : 'bg-purple-100'
                  }`}>
                  <Icon name={point?.icon} size={24} />
                </div>
                <div className={`ml-4 ${language === 'fa' ? 'mr-4 ml-0' : ''}`}>
                  <h3 className="font-semibold text-lg">{point?.title}</h3>
                  <div className="text-2xl font-bold">{point?.stat}</div>
                </div>
              </div>
              <p className="text-sm leading-relaxed">{point?.description}</p>
            </div>
          ))}
        </div>

        {/* Before/After Toggle */}
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg p-1 shadow-md">
              <Button
                variant={activeView === 'before' ? 'default' : 'ghost'}
                onClick={() => setActiveView('before')}
                className="px-6 py-3">
                {currentContent?.toggleBefore}
              </Button>
              <Button
                variant={activeView === 'after' ? 'default' : 'ghost'}
                onClick={() => setActiveView('after')}
                className="px-6 py-3">
                {currentContent?.toggleAfter}
              </Button>
            </div>
          </div>

          {/* Scenario Display */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {activeView === 'before' ? (
              <div
                className={`${language === 'fa' ? 'text-right' : 'text-left'}`}>
                <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                  <Icon
                    name="AlertCircle"
                    size={28}
                    className={`${language === 'fa' ? 'ml-3' : 'mr-3'}`}
                  />
                  {currentContent?.beforeScenario?.title}
                </h3>
                <div className="space-y-4">
                  {currentContent?.beforeScenario?.points?.map(
                    (point, index) => (
                      <div key={index} className="flex items-start">
                        <Icon
                          name="X"
                          size={20}
                          color="#DC2626"
                          className={`${
                            language === 'fa' ? 'ml-3 mt-1' : 'mr-3 mt-1'
                          }`}
                        />
                        <p className="text-gray-700">{point}</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div
                className={`${language === 'fa' ? 'text-right' : 'text-left'}`}>
                <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                  <Icon
                    name="CheckCircle"
                    size={28}
                    className={`${language === 'fa' ? 'ml-3' : 'mr-3'}`}
                  />
                  {currentContent?.afterScenario?.title}
                </h3>
                <div className="space-y-4">
                  {currentContent?.afterScenario?.points?.map(
                    (point, index) => (
                      <div key={index} className="flex items-start">
                        <Icon
                          name="Check"
                          size={20}
                          color="#059669"
                          className={`${
                            language === 'fa' ? 'ml-3 mt-1' : 'mr-3 mt-1'
                          }`}
                        />
                        <p className="text-gray-700">{point}</p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
